import React, { useState, useMemo } from 'react';
import { ChevronLeft, TrendingUp, TrendingDown, Award, Flame, Target, Zap, Calendar, Shield } from 'lucide-react';
import {
  BarChart, Bar, Cell, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  getLocalDateStr, addDays, completionRate, isHabitDueOnDate, getDayOfWeek,
  calculateStreak, calculateBestStreak, getWeekNumber
} from '../../lib/habitUtils';

const WEEKDAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const ACCENT_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold text-emerald-400">
          {p.name}: {p.value}{suffix}
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, iconColor, label, value, sub, className = '' }) => (
  <div className={`rounded-xl p-3 backdrop-blur-sm bg-white/5 border border-white/10 ${className}`}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      <span className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xl font-bold text-white">{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

const TimeFilter = ({ value, onChange }) => (
  <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
    {[
      { key: 7, label: '7T' },
      { key: 30, label: '30T' },
      { key: 90, label: '90T' }
    ].map(t => (
      <button
        key={t.key}
        onClick={() => onChange(t.key)}
        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
          value === t.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-500 hover:text-slate-300'
        }`}
        data-testid={`filter-${t.key}d`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

// Mini donut for overall completion
const CompletionDonut = ({ rate, size = 120, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (rate / 100) * circumference;
  const color = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-white">{rate}%</span>
      </div>
    </div>
  );
};

// Speedometer gauge for monthly rates
const SpeedoGauge = ({ rate, label, size = 100 }) => {
  const cx = size / 2;
  const cy = size / 2 + 5;
  const r = size / 2 - 8;
  const startAngle = Math.PI;
  const endAngle = 0;
  const sweepAngle = startAngle - (rate / 100) * Math.PI;

  // Arc path for background
  const arcPath = (start, end) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy - r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy - r * Math.sin(end);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
  };

  // Needle position
  const needleAngle = Math.PI - (rate / 100) * Math.PI;
  const needleLen = r - 8;
  const nx = cx + needleLen * Math.cos(needleAngle);
  const ny = cy - needleLen * Math.sin(needleAngle);

  const color = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 18} viewBox={`0 0 ${size} ${size / 2 + 18}`}>
        {/* Background arc */}
        <path d={arcPath(startAngle, endAngle)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={8} strokeLinecap="round" />
        {/* Filled arc */}
        <path d={arcPath(startAngle, sweepAngle)} fill="none" stroke={color} strokeWidth={8} strokeLinecap="round"
          className="transition-all duration-700 ease-out" />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="white" strokeWidth={2} strokeLinecap="round" opacity={0.8} />
        <circle cx={cx} cy={cy} r={3} fill="white" opacity={0.8} />
        {/* Rate text */}
        <text x={cx} y={cy - 10} textAnchor="middle" fill={color} fontSize="16" fontWeight="bold">{rate}%</text>
      </svg>
      <p className="text-[10px] text-slate-400 -mt-1">{label}</p>
    </div>
  );
};

export default function HabitInsights({ habits, allLogs, onBack }) {
  const [selectedHabitId, setSelectedHabitId] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const activeHabits = habits.filter(h => h.status !== 'archived' && h.status !== 'paused');
  const selectedHabit = selectedHabitId ? habits.find(h => h.id === selectedHabitId) : null;
  const selectedLogs = selectedHabitId ? (allLogs[selectedHabitId] || []) : [];

  // --- GLOBAL STATS ---
  const globalStats = useMemo(() => {
    const today = getLocalDateStr();
    let totalDue = 0, totalDone = 0;
    for (let i = 0; i < timeRange; i++) {
      const date = addDays(today, -i);
      activeHabits.forEach(h => {
        if (isHabitDueOnDate(h, date) && date >= h.startDate) {
          totalDue++;
          const logs = allLogs[h.id] || [];
          if (logs.some(l => l.date === date && l.done)) totalDone++;
        }
      });
    }
    const rate = totalDue > 0 ? Math.round((totalDone / totalDue) * 100) : 0;

    const totalCompletions = Object.values(allLogs).flat().filter(l => l.done).length;
    const bestStreakHabit = activeHabits.reduce((best, h) => {
      const s = calculateStreak(h, allLogs[h.id] || []);
      return s > (best?.streak || 0) ? { ...h, streak: s } : best;
    }, null);

    return { rate, totalDone, totalDue, totalCompletions, bestStreakHabit, activeCount: activeHabits.length };
  }, [activeHabits, allLogs, timeRange]);

  // --- TREND DATA (daily points) ---
  const trendData = useMemo(() => {
    const today = getLocalDateStr();
    const data = [];
    for (let i = timeRange - 1; i >= 0; i--) {
      const date = addDays(today, -i);
      const dayLabel = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      let due = 0, done = 0;
      activeHabits.forEach(h => {
        if (isHabitDueOnDate(h, date) && date >= h.startDate) {
          due++;
          if ((allLogs[h.id] || []).some(l => l.date === date && l.done)) done++;
        }
      });
      data.push({ date: dayLabel, rate: due > 0 ? Math.round((done / due) * 100) : 0, done, due });
    }
    return data;
  }, [activeHabits, allLogs, timeRange]);

  // --- WEEKLY AGGREGATION ---
  const weeklyData = useMemo(() => {
    const today = getLocalDateStr();
    const weeks = {};
    for (let i = 0; i < timeRange; i++) {
      const date = addDays(today, -i);
      const wk = getWeekNumber(date);
      const yr = new Date(date + 'T12:00:00').getFullYear();
      const key = `${yr}-KW${wk}`;
      if (!weeks[key]) weeks[key] = { label: `KW${wk}`, due: 0, done: 0 };
      activeHabits.forEach(h => {
        if (isHabitDueOnDate(h, date) && date >= h.startDate) {
          weeks[key].due++;
          if ((allLogs[h.id] || []).some(l => l.date === date && l.done)) weeks[key].done++;
        }
      });
    }
    return Object.values(weeks).reverse().map(w => ({
      ...w,
      rate: w.due > 0 ? Math.round((w.done / w.due) * 100) : 0
    }));
  }, [activeHabits, allLogs, timeRange]);

  // --- WEEKDAY ANALYSIS ---
  const weekdayData = useMemo(() => {
    const today = getLocalDateStr();
    const counts = Array(7).fill(null).map(() => ({ due: 0, done: 0 }));
    for (let i = 0; i < timeRange; i++) {
      const date = addDays(today, -i);
      const dow = getDayOfWeek(date);
      activeHabits.forEach(h => {
        if (isHabitDueOnDate(h, date) && date >= h.startDate) {
          counts[dow].due++;
          if ((allLogs[h.id] || []).some(l => l.date === date && l.done)) counts[dow].done++;
        }
      });
    }
    return [1, 2, 3, 4, 5, 6, 0].map(dow => ({
      day: WEEKDAY_LABELS[dow],
      rate: counts[dow].due > 0 ? Math.round((counts[dow].done / counts[dow].due) * 100) : 0,
      done: counts[dow].done,
      due: counts[dow].due
    }));
  }, [activeHabits, allLogs, timeRange]);

  // --- MONTHLY GAUGE DATA (global) ---
  const monthlyGaugeData = useMemo(() => {
    const today = getLocalDateStr();
    const todayDate = new Date(today + 'T12:00:00');
    const months = [];
    for (let m = 2; m >= 0; m--) {
      const monthDate = new Date(todayDate.getFullYear(), todayDate.getMonth() - m, 1);
      const monthEnd = new Date(todayDate.getFullYear(), todayDate.getMonth() - m + 1, 0);
      const label = monthDate.toLocaleDateString('de-DE', { month: 'short' });
      let due = 0, done = 0;
      for (let d = new Date(monthDate); d <= monthEnd && d <= todayDate; d.setDate(d.getDate() + 1)) {
        const dateStr = getLocalDateStr(d);
        activeHabits.forEach(h => {
          if (isHabitDueOnDate(h, dateStr) && dateStr >= h.startDate) {
            due++;
            if ((allLogs[h.id] || []).some(l => l.date === dateStr && l.done)) done++;
          }
        });
      }
      months.push({ label, rate: due > 0 ? Math.round((done / due) * 100) : 0 });
    }
    return months;
  }, [activeHabits, allLogs]);


  // --- HABIT STABILITY OVERVIEW ---
  const stabilityData = useMemo(() => {
    const today = getLocalDateStr();
    let gefestigt = 0, aufbau = 0, instabil = 0;
    const details = { gefestigt: [], aufbau: [], instabil: [] };

    activeHabits.forEach(h => {
      const startD = new Date(h.startDate + 'T12:00:00');
      const todayD = new Date(today + 'T12:00:00');
      const ageDays = Math.floor((todayD - startD) / (1000 * 60 * 60 * 24));
      const logs = allLogs[h.id] || [];

      // Calculate last 30 days rate (or 8 weeks if < 7 occurrences)
      let windowDays = 30;
      let due = 0, done = 0;
      for (let i = 0; i < windowDays; i++) {
        const date = addDays(today, -i);
        if (isHabitDueOnDate(h, date) && date >= h.startDate) {
          due++;
          if (logs.some(l => l.date === date && l.done)) done++;
        }
      }
      // If < 7 planned occurrences, extend to 8 weeks
      if (due < 7) {
        due = 0; done = 0;
        windowDays = 56;
        for (let i = 0; i < windowDays; i++) {
          const date = addDays(today, -i);
          if (isHabitDueOnDate(h, date) && date >= h.startDate) {
            due++;
            if (logs.some(l => l.date === date && l.done)) done++;
          }
        }
      }
      const rate = due > 0 ? Math.round((done / due) * 100) : 0;

      if (rate < 50) {
        instabil++;
        details.instabil.push(h.title);
      } else if (ageDays >= 60 && rate >= 75) {
        gefestigt++;
        details.gefestigt.push(h.title);
      } else {
        aufbau++;
        details.aufbau.push(h.title);
      }
    });

    const total = gefestigt + aufbau + instabil;
    return { gefestigt, aufbau, instabil, total, details };
  }, [activeHabits, allLogs]);

  // --- HABIT RANKING ---
  const habitRanking = useMemo(() => {
    return activeHabits.map(h => ({
      ...h,
      rate: completionRate(h, allLogs[h.id] || [], timeRange),
      streak: calculateStreak(h, allLogs[h.id] || []),
      bestStreak: calculateBestStreak(h, allLogs[h.id] || [])
    })).sort((a, b) => b.rate - a.rate);
  }, [activeHabits, allLogs, timeRange]);

  // --- PER-HABIT DETAIL ---
  const habitRateData = useMemo(() => {
    if (!selectedHabit) return [];
    const today = getLocalDateStr();
    return Array.from({ length: timeRange }, (_, idx) => {
      const i = timeRange - 1 - idx;
      const date = addDays(today, -i);
      const dayLabel = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      let due = 0, done = 0;
      for (let j = 0; j < 7; j++) {
        const d = addDays(date, -j);
        if (isHabitDueOnDate(selectedHabit, d) && d >= selectedHabit.startDate) {
          due++;
          if (selectedLogs.some(l => l.date === d && l.done)) done++;
        }
      }
      return { date: dayLabel, rate: due > 0 ? Math.round((done / due) * 100) : 0 };
    });
  }, [selectedHabit, selectedLogs, timeRange]);

  const habitWeekdayData = useMemo(() => {
    if (!selectedHabit) return [];
    const today = getLocalDateStr();
    const counts = Array(7).fill(null).map(() => ({ due: 0, done: 0 }));
    for (let i = 0; i < timeRange; i++) {
      const date = addDays(today, -i);
      const dow = getDayOfWeek(date);
      if (isHabitDueOnDate(selectedHabit, date) && date >= selectedHabit.startDate) {
        counts[dow].due++;
        if (selectedLogs.some(l => l.date === date && l.done)) counts[dow].done++;
      }
    }
    return [1, 2, 3, 4, 5, 6, 0].map(dow => ({
      day: WEEKDAY_LABELS[dow],
      rate: counts[dow].due > 0 ? Math.round((counts[dow].done / counts[dow].due) * 100) : 0
    }));
  }, [selectedHabit, selectedLogs, timeRange]);

  const valueTrend = useMemo(() => {
    if (!selectedHabit || selectedHabit.type !== 'performance') return [];
    return selectedLogs
      .filter(l => l.done && l.value != null)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-timeRange)
      .map(l => ({
        date: new Date(l.date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
        value: l.value
      }));
  }, [selectedHabit, selectedLogs, timeRange]);

  // --- PER-HABIT MONTHLY GAUGES ---
  const habitMonthlyGauge = useMemo(() => {
    if (!selectedHabit) return [];
    const today = getLocalDateStr();
    const todayDate = new Date(today + 'T12:00:00');
    const months = [];
    for (let m = 2; m >= 0; m--) {
      const monthDate = new Date(todayDate.getFullYear(), todayDate.getMonth() - m, 1);
      const monthEnd = new Date(todayDate.getFullYear(), todayDate.getMonth() - m + 1, 0);
      const label = monthDate.toLocaleDateString('de-DE', { month: 'short' });
      let due = 0, done = 0;
      for (let d = new Date(monthDate); d <= monthEnd && d <= todayDate; d.setDate(d.getDate() + 1)) {
        const dateStr = getLocalDateStr(d);
        if (isHabitDueOnDate(selectedHabit, dateStr) && dateStr >= selectedHabit.startDate) {
          due++;
          if (selectedLogs.some(l => l.date === dateStr && l.done)) done++;
        }
      }
      months.push({ label, rate: due > 0 ? Math.round((done / due) * 100) : 0 });
    }
    return months;
  }, [selectedHabit, selectedLogs]);

  // --- RENDER: SINGLE HABIT ---
  if (selectedHabit) {
    const hRate = completionRate(selectedHabit, selectedLogs, timeRange);
    const hStreak = calculateStreak(selectedHabit, selectedLogs);
    return (
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 space-y-4" data-testid="habit-insights-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelectedHabitId(null)} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" data-testid="insights-detail-back">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-base font-bold text-white">{selectedHabit.title}</h2>
              <p className="text-xs text-slate-500">{selectedHabit.type === 'binary' ? 'Einfach erledigen' : 'Zählen & steigern'}</p>
            </div>
          </div>
          <TimeFilter value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-2">
          <StatCard icon={Target} iconColor="text-emerald-400" label="Rate" value={`${hRate}%`} sub={`${timeRange}T Zeitraum`} />
          <StatCard icon={Flame} iconColor="text-orange-400" label="Streak" value={hStreak} sub="Tage" />
          <StatCard icon={Award} iconColor="text-purple-400" label="Gesamt" value={selectedLogs.filter(l => l.done).length} sub="Check-ins" />
        </div>

        {/* Rolling rate area chart */}
        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Erledigungsrate (7T rollierend)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={habitRateData}>
              <defs>
                <linearGradient id="habitGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} interval={Math.max(0, Math.floor(timeRange / 7))} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} width={30} />
              <Tooltip content={<CustomTooltip suffix="%" />} />
              <Area type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={2} fill="url(#habitGrad)" connectNulls name="Rate" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Weekday bar chart */}
        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Wochentag-Analyse</h3>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={habitWeekdayData}>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#10b981' }} width={30} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip suffix="%" />} />
              <Bar dataKey="rate" radius={[6, 6, 0, 0]} name="Rate">
                {habitWeekdayData.map((entry, i) => (
                  <Cell key={i} fill={entry.rate >= 70 ? '#10b981' : entry.rate >= 40 ? '#f59e0b' : '#ef4444'} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Gauges */}
        {habitMonthlyGauge.length > 0 && (
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Monatliche Erfüllung</h3>
            <div className="flex justify-around">
              {habitMonthlyGauge.map((m, i) => (
                <SpeedoGauge key={i} rate={m.rate} label={m.label} size={90} />
              ))}
            </div>
          </div>
        )}

        {/* Performance value trend */}
        {selectedHabit.type === 'performance' && valueTrend.length > 0 && (
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Werte-Trend ({selectedHabit.tracking?.unitLabel})
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={valueTrend}>
                <defs>
                  <linearGradient id="valueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} interval={Math.max(0, Math.floor(valueTrend.length / 5))} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} width={35} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#valueGrad)" dot={{ r: 3, fill: '#3b82f6' }} name="Wert" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  }

  // --- RENDER: GLOBAL DASHBOARD ---
  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 space-y-4" data-testid="habit-insights-global">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" data-testid="insights-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-base font-bold text-white">Insights</h2>
        </div>
        <TimeFilter value={timeRange} onChange={setTimeRange} />
      </div>

      {activeHabits.length === 0 ? (
        <div className="rounded-xl bg-white/5 border border-white/10 p-8 text-center">
          <p className="text-slate-500 text-sm">Erstelle zuerst Habits, um Insights zu sehen.</p>
        </div>
      ) : (
        <>
          {/* Top Stats Row */}
          <div className="flex items-center gap-4">
            {/* Donut */}
            <div className="flex-shrink-0">
              <CompletionDonut rate={globalStats.rate} />
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2">
              <StatCard icon={Zap} iconColor="text-emerald-400" label="Erledigt" value={globalStats.totalDone} sub={`von ${globalStats.totalDue}`} />
              <StatCard icon={Flame} iconColor="text-orange-400" label="Top Streak"
                value={globalStats.bestStreakHabit?.streak || 0}
                sub={globalStats.bestStreakHabit?.title?.slice(0, 12) || '-'} />
              <StatCard icon={Target} iconColor="text-blue-400" label="Aktiv" value={globalStats.activeCount} sub="Habits" />
              <StatCard icon={Award} iconColor="text-purple-400" label="Gesamt" value={globalStats.totalCompletions} sub="Check-ins" />
            </div>
          </div>

          {/* Trend Area Chart */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Täglicher Trend</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} interval={Math.max(0, Math.floor(timeRange / 7))} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} width={30} />
                <Tooltip content={<CustomTooltip suffix="%" />} />
                <Area type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={2} fill="url(#trendGrad)" connectNulls name="Rate" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Weekly Progress Bars */}
          {weeklyData.length > 1 && (
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-blue-400" />
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Wöchentlicher Fortschritt</h3>
              </div>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={weeklyData}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#10b981' }} width={30} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip suffix="%" />} />
                  <Bar dataKey="rate" radius={[6, 6, 0, 0]} name="Rate" label={{ fill: '#10b981', fontSize: 10, position: 'top' }}>
                    {weeklyData.map((entry, i) => (
                      <Cell key={i} fill={entry.rate >= 70 ? '#10b981' : entry.rate >= 40 ? '#f59e0b' : '#ef4444'} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly Gauges */}
          {monthlyGaugeData.length > 0 && (
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Monatliche Erfüllung</h3>
              <div className="flex justify-around">
                {monthlyGaugeData.map((m, i) => (
                  <SpeedoGauge key={i} rate={m.rate} label={m.label} />
                ))}
              </div>
            </div>
          )}

          {/* Two charts side by side */}
          <div className="grid grid-cols-2 gap-3">
            {/* Weekday Radar */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Wochentage</h3>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={weekdayData} barSize={16}>
                  <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip content={<CustomTooltip suffix="%" />} />
                  <Bar dataKey="rate" radius={[4, 4, 0, 0]} name="Rate">
                    {weekdayData.map((entry, i) => (
                      <Cell key={i} fill={entry.rate >= 70 ? '#10b981' : entry.rate >= 40 ? '#f59e0b' : '#ef4444'} fillOpacity={0.7} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Habit Stability Overview */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Stabilität</h3>
              </div>
              {stabilityData.total > 0 ? (
                <div className="space-y-2">
                  {/* Stacked bar */}
                  <div className="flex rounded-full h-5 overflow-hidden bg-white/5">
                    {stabilityData.gefestigt > 0 && (
                      <div
                        className="bg-emerald-500 flex items-center justify-center transition-all"
                        style={{ width: `${(stabilityData.gefestigt / stabilityData.total) * 100}%` }}
                      >
                        <span className="text-[9px] font-bold text-white">{stabilityData.gefestigt}</span>
                      </div>
                    )}
                    {stabilityData.aufbau > 0 && (
                      <div
                        className="bg-amber-500 flex items-center justify-center transition-all"
                        style={{ width: `${(stabilityData.aufbau / stabilityData.total) * 100}%` }}
                      >
                        <span className="text-[9px] font-bold text-white">{stabilityData.aufbau}</span>
                      </div>
                    )}
                    {stabilityData.instabil > 0 && (
                      <div
                        className="bg-red-500 flex items-center justify-center transition-all"
                        style={{ width: `${(stabilityData.instabil / stabilityData.total) * 100}%` }}
                      >
                        <span className="text-[9px] font-bold text-white">{stabilityData.instabil}</span>
                      </div>
                    )}
                  </div>
                  {/* Legend */}
                  <div className="flex gap-3 text-[9px]">
                    <span className="flex items-center gap-1 text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Gefestigt</span>
                    <span className="flex items-center gap-1 text-amber-400"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Aufbau</span>
                    <span className="flex items-center gap-1 text-red-400"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />Instabil</span>
                  </div>
                  {/* Insight text */}
                  <div className="text-[10px] text-slate-500 space-y-0.5">
                    {stabilityData.gefestigt > 0 && (
                      <p>{stabilityData.gefestigt} {stabilityData.gefestigt === 1 ? 'Gewohnheit ist' : 'Gewohnheiten sind'} bereits stabil.</p>
                    )}
                    {stabilityData.aufbau > 0 && (
                      <p>{stabilityData.aufbau} {stabilityData.aufbau === 1 ? 'Gewohnheit befindet' : 'Gewohnheiten befinden'} sich im Aufbau.</p>
                    )}
                    {stabilityData.instabil > 0 && (
                      <p className="text-red-400/70">{stabilityData.instabil} instabil – evtl. Ziel anpassen?</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-[10px] text-slate-600">Keine aktiven Habits</p>
              )}
            </div>
          </div>

          {/* Habit Ranking */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-amber-400" />
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Habit Ranking ({timeRange}T)</h3>
            </div>
            <div className="space-y-2">
              {habitRanking.map((h, i) => (
                <button
                  key={h.id}
                  onClick={() => setSelectedHabitId(h.id)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors text-left group"
                  data-testid={`ranking-habit-${i}`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-amber-500/20 text-amber-400' :
                    i < 3 ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-white/5 text-slate-500'
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate group-hover:text-white">{h.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {h.streak > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-orange-400">
                          <Flame className="w-3 h-3" /> {h.streak}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-16 bg-white/5 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${h.rate >= 70 ? 'bg-emerald-500' : h.rate >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                        style={{ width: `${h.rate}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono font-medium text-slate-400 w-8 text-right">{h.rate}%</span>
                    <ChevronLeft className="w-4 h-4 text-slate-600 rotate-180 group-hover:text-slate-400" />
                  </div>
                </button>
              ))}
            </div>

            {/* Tip for lowest habit */}
            {habitRanking.length > 0 && habitRanking[habitRanking.length - 1].rate < 50 && (
              <div className="mt-3 p-3 bg-amber-500/10 rounded-lg flex items-start gap-2 border border-amber-500/20">
                <TrendingDown className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-300/80">
                  Tipp: Reduziere "{habitRanking[habitRanking.length - 1].title}" auf die 1%-Version. Konsistenz schlägt Intensität.
                </p>
              </div>
            )}
          </div>

          {/* Per-Habit Quick Select */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Einzelanalyse</h3>
            <div className="space-y-1">
              {activeHabits.map(h => {
                const hRate = completionRate(h, allLogs[h.id] || [], timeRange);
                return (
                  <button
                    key={h.id}
                    onClick={() => setSelectedHabitId(h.id)}
                    className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-white/5 text-left transition-colors group"
                    data-testid={`select-habit-${h.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${hRate >= 70 ? 'bg-emerald-500' : hRate >= 40 ? 'bg-amber-500' : 'bg-red-400'}`} />
                      <span className="text-sm text-slate-300 group-hover:text-white">{h.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{hRate}%</span>
                      <ChevronLeft className="w-4 h-4 text-slate-600 rotate-180 group-hover:text-slate-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
